/**
 * sdkRealtimeProvider — Supabase-realtime backend for the SDK's
 * `workspaceProject.realtime.*` API surface.
 *
 * Wired into the SDK at boot via
 * `(await sdk.getService('workspaceProject')).setRealtimeProvider(supabaseRealtimeProvider)`.
 * Once installed, every consumer using
 *   `sdk.execute('workspaceProject.realtime.<entity>', 'subscribe', filter, cb)`
 * gets live events through the governance Supabase realtime channel — no
 * other code path needed.
 *
 * Why this is here and not in the SDK package: the provider depends on
 * `@symbo.ls/sdk-bridge`'s `getRealtimeSupabase()` (browser-tier WebSocket
 * client). Keeping the wire-protocol adapter in the workspace shell keeps
 * the SDK transport-agnostic — when backend ships native socket.io
 * realtime alongside CollabService, the workspace can swap providers
 * without touching the SDK or any consumer.
 *
 * Provider contract (from WorkspaceProjectService):
 *   ({ op, filter, callback }) → unsubscribe fn
 *
 * Op vocabulary + table mapping:
 *   'chat.messages'     → postgres_changes on `chat_messages`,
 *                         filtered by `channel_id` when supplied
 *   'chat.channels'     → postgres_changes on `chat_channels` + `chat_channel_members`
 *   'chat.mentions'     → postgres_changes INSERT on `chat_mentions`,
 *                         filtered by `mentioned_email`
 *   'tickets'           → postgres_changes on `tickets`
 *   'notifications'     → postgres_changes INSERT on `notifications`,
 *                         filtered by `user_email`
 *   'presence'          → Supabase presence channel (sync/join/leave),
 *                         distinct from postgres_changes
 *   'meet'              → composite: `meet_rooms` + `meet_participants` +
 *                         `meet_waiting_room`, filtered by `room_id`
 *   'agentMessages'     → postgres_changes INSERT on `agent_messages`,
 *                         filtered by `to_agent`
 */

import { getRealtimeSupabase } from './supabaseClient.js'

const _channelName = (op) =>
  `sdk-${op}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

// Translate Supabase postgres_changes payload → SDK callback.
// Supabase delivers { eventType: 'INSERT' | 'UPDATE' | 'DELETE', new, old, ... }
// SDK callback expects (eventType, payload). Pass the whole payload through
// so consumers that need .new/.old still have them.
const _pgForward = (cb) => (payload) => {
  try { cb(payload?.eventType || 'INSERT', payload) } catch (_) { /* listener errors don't propagate */ }
}

export const supabaseRealtimeProvider = ({ op, filter = {}, callback }) => {
  const sb = getRealtimeSupabase()
  if (!sb) return () => {}
  if (typeof callback !== 'function') return () => {}

  // Presence channel — keyed on userKey so two browsers from the same user
  // appear as one presence entry.
  const presenceConfig = op === 'presence' && filter.userKey
    ? { config: { presence: { key: String(filter.userKey) } } }
    : undefined
  const channel = sb.channel(_channelName(op), presenceConfig)

  switch (op) {
    case 'chat.messages': {
      const flt = filter.channelId ? { filter: `channel_id=eq.${filter.channelId}` } : {}
      channel.on('postgres_changes',
        { event: '*', schema: 'public', table: 'chat_messages', ...flt },
        _pgForward(callback))
      break
    }

    case 'chat.channels': {
      channel.on('postgres_changes',
        { event: '*', schema: 'public', table: 'chat_channels' },
        _pgForward(callback))
      channel.on('postgres_changes',
        { event: '*', schema: 'public', table: 'chat_channel_members' },
        (payload) => {
          // Differentiate channel-row vs membership-row events so listeners
          // can route updates correctly. Tag the table on the payload.
          try {
            callback(payload?.eventType || 'INSERT', { ...payload, _kind: 'member' })
          } catch (_) {}
        })
      break
    }

    case 'chat.mentions': {
      const flt = filter.userEmail ? { filter: `mentioned_email=eq.${filter.userEmail}` } : {}
      channel.on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_mentions', ...flt },
        _pgForward(callback))
      break
    }

    case 'tickets': {
      // Multi-table feed for ticket triage — `tickets` plus optional
      // `agent_presence` (live-heartbeat status drives "Now" vs "Ongoing"
      // buckets) and `agent_activity_log` (picked_up / shipped events).
      // Consumers pick the table set via `filter.tables`; default is
      // tickets-only so cheap subscribers don't get presence noise.
      // Each callback is tagged by table kind so listeners route events
      // without re-reading payload.table.
      const requested = Array.isArray(filter.tables) && filter.tables.length
        ? new Set(filter.tables)
        : new Set(['tickets'])
      const flt = filter.where ? { filter: filter.where } : {}

      if (requested.has('tickets')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'tickets', ...flt },
          (payload) => {
            try { callback(payload?.eventType || 'INSERT', { ...payload, _kind: 'tickets' }) }
            catch (_) {}
          })
      }
      if (requested.has('agent_presence') || requested.has('presence')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'agent_presence' },
          (payload) => {
            try { callback(payload?.eventType || 'INSERT', { ...payload, _kind: 'agent_presence' }) }
            catch (_) {}
          })
      }
      if (requested.has('agent_activity_log') || requested.has('activity')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'agent_activity_log' },
          (payload) => {
            try { callback(payload?.eventType || 'INSERT', { ...payload, _kind: 'agent_activity_log' }) }
            catch (_) {}
          })
      }
      break
    }

    case 'notifications': {
      const flt = filter.userEmail ? { filter: `user_email=eq.${filter.userEmail}` } : {}
      channel.on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', ...flt },
        _pgForward(callback))
      break
    }

    case 'presence': {
      channel.on('presence', { event: 'sync' }, () => {
        try { callback('sync', { state: channel.presenceState() }) } catch (_) {}
      })
      channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
        try { callback('join', { key, newPresences }) } catch (_) {}
      })
      channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        try { callback('leave', { key, leftPresences }) } catch (_) {}
      })
      // Track this user once subscribed — Supabase presence requires it.
      channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && filter.userKey) {
          try {
            await channel.track({
              status: 'online',
              online_at: new Date().toISOString(),
              ...(filter.meta || {}),
            })
          } catch (_) {}
        }
      })
      // Early return — presence channel is already subscribed.
      return () => {
        try { channel.unsubscribe() } catch (_) {}
      }
    }

    case 'meet': {
      // Five meet tables compose the live-room feed. Consumer can pick a
      // subset via `filter.tables`; default is the rooms/participants/
      // waiting trio (the home-page sidebar use case). Transcript + analysis
      // tables are opt-in because they're high-volume and only useful on
      // an active call's transcript page.
      const requested = Array.isArray(filter.tables) && filter.tables.length
        ? new Set(filter.tables)
        : new Set(['rooms', 'participants', 'waiting'])
      const roomFilter = filter.roomId ? `room_id=eq.${filter.roomId}` : null
      const idFilter = filter.roomId ? `id=eq.${filter.roomId}` : null

      if (requested.has('rooms')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'meet_rooms',
            ...(idFilter ? { filter: idFilter } : {}) },
          (payload) => { try { callback('room', payload) } catch (_) {} })
      }
      if (requested.has('participants')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'meet_participants',
            ...(roomFilter ? { filter: roomFilter } : {}) },
          (payload) => { try { callback('participant', payload) } catch (_) {} })
      }
      if (requested.has('waiting')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'meet_waiting_room',
            ...(roomFilter ? { filter: roomFilter } : {}) },
          (payload) => { try { callback('waiting', payload) } catch (_) {} })
      }
      if (requested.has('transcripts')) {
        channel.on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'meet_transcripts',
            ...(roomFilter ? { filter: roomFilter } : {}) },
          (payload) => { try { callback('transcript', payload) } catch (_) {} })
      }
      if (requested.has('analyses')) {
        channel.on('postgres_changes',
          { event: '*', schema: 'public', table: 'meet_transcript_analyses',
            ...(roomFilter ? { filter: roomFilter } : {}) },
          (payload) => { try { callback('analysis', payload) } catch (_) {} })
      }
      break
    }

    case 'agentMessages': {
      const flt = filter.toAgent ? { filter: `to_agent=eq.${filter.toAgent}` } : {}
      channel.on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'agent_messages', ...flt },
        _pgForward(callback))
      break
    }

    default:
      // Unknown op — return noop unsub so caller doesn't crash.
      return () => {}
  }

  // Subscribe non-presence channels (presence path returned above).
  channel.subscribe()

  return () => {
    try { channel.unsubscribe() } catch (_) {}
  }
}
